import { ref } from "vue";
import { Cluster } from "./clusterModel";
import { findRowInsertIndex, getRowConflicts } from "./clusterDragHelper";
import {
  DEFAULT_EMPTY_ROWS,
  DEFAULT_GRID_GROUP,
  DEFAULT_VISIBLE_CLUSTER_ROWS,
  GRID_OPEN_FIRST_X_GROUPS,
} from "./learningObjectivesConstants";

export const createGridManager = () => {
  const grid = ref([]);
  const weeks = ref([]);
  const isInitialized = ref(false);
  const hasGridGroups = ref(false);
  const isRendered = ref(false);

  const initializeGrid = (items, _weeks, groups = null) => {
    weeks.value = _weeks;

    // Generate the grid without groups
    if (!groups || groups.length === 0) {
      const rows = generateGridRows(items);

      rows.forEach((row) => grid.value.push(row));

      items.forEach((item) => {
        addGridItem(item);
      });

      return grid.value;
    }

    // Set the flag to indicate that the grid has groups
    hasGridGroups.value = true;

    // Generate the grid with groups
    grid.value = groups.map((group, index) => {
      const {
        id,
        meta: { subject = null, educationLevel = null },
      } = group;

      group.rows = generateGridRows(
        items.filter(
          ({ subject_shortage, planningMeta: { groupId } }) =>
            groupId && groupId === id && subject_shortage === subject,
        ),
        index,
        { groupId: id, subjectShortage: subject, educationLevel },
      );

      return group;
    });

    items.forEach((item) => {
      addGridItem(item);
    });

    // Open the first X groups
    for (
      let index = 0;
      index < Math.min(grid.value.length, GRID_OPEN_FIRST_X_GROUPS);
      index++
    ) {
      toggleGroupVisibility(grid.value[index]);
    }

    return grid.value;
  };

  // Regenerate every group's rows from the given clusters (placed by their
  // planningMeta.rowIndex), keeping the existing groups and their visibility.
  // Used to revert the grid after a discarded add without a full re-init.
  const rebuildGridFromClusters = (items) => {
    if (!hasGridGroups.value) {
      grid.value = [];

      const rows = generateGridRows(items);

      rows.forEach((row) => grid.value.push(row));
      items.forEach((item) => addGridItem(item));

      return;
    }

    grid.value.forEach((group, index) => {
      group.rows = generateGridRows(
        items.filter(
          ({ subject_shortage, planningMeta: { groupId } }) =>
            groupId &&
            groupId === group.id &&
            subject_shortage === group.meta.subject,
        ),
        index,
        {
          groupId: group.id,
          subjectShortage: group.meta.subject,
          educationLevel: group.meta.educationLevel,
        },
      );
    });

    items.forEach((item) => addGridItem(item));
  };

  // Regenerate a single group's rows and re-place its clusters — used during a
  // live move so only the dragged klas redraws (clusters never cross klassen).
  const rebuildGroupFromClusters = (groupIndex, items) => {
    const group = grid.value[groupIndex];

    if (!group) {
      return;
    }

    const groupClusters = items.filter(
      (item) =>
        item.planningMeta.groupId === group.id &&
        item.subject_shortage === group.meta.subject,
    );

    group.rows = generateGridRows(groupClusters, groupIndex, {
      groupId: group.id,
      subjectShortage: group.meta.subject,
      educationLevel: group.meta.educationLevel,
    });

    groupClusters.forEach((item) => addGridItem(item));
  };

  const generateGridRows = (
    items,
    groupIndex = null,
    additionalData = null,
  ) => {
    const rows = [];

    const rowCount = generateRowSize(items);

    // Always keep one empty trailing row (DEFAULT_EMPTY_ROWS), but never show
    // fewer than DEFAULT_VISIBLE_CLUSTER_ROWS slots per class.
    const totalRows = Math.max(
      rowCount + DEFAULT_EMPTY_ROWS + 1,
      DEFAULT_VISIBLE_CLUSTER_ROWS,
    );

    for (let i = 0; i < totalRows; i++) {
      rows.push(generateClusterRow(i, groupIndex, additionalData));
    }

    return rows;
  };

  const generateClusterRow = (
    rowIndex,
    groupIndex = null,
    additionalData = null,
  ) => {
    return weeks.value.map((_, index) => ({
      id: `${rowIndex}-${index}`,
      weekIndex: index,
      rowIndex,
      ...(groupIndex !== null && { groupIndex }),
      ...(additionalData !== null && { additionalData }),
    }));
  };

  const generateWeekArray = (
    rowIndex,
    weekIndexes,
    groupIndex = null,
    additionalData = null,
  ) => {
    return weekIndexes.map((weekIndex) => ({
      id: `${rowIndex}-${weekIndex}`,
      weekIndex,
      rowIndex,
      ...(groupIndex !== null && { groupIndex }),
      ...(additionalData !== null && { additionalData }),
    }));
  };

  const generateGridGroup = ({ id, name, meta }) => {
    if (!id || !name) {
      throw new Error("Id and name are required to create a grid group");
    }

    return Object.assign({}, DEFAULT_GRID_GROUP, { id, name, meta });
  };

  const toggleGroupVisibility = (group) => {
    group.meta.isVisible = !group.meta.isVisible;

    return group.meta.isVisible;
  };

  const findGridGroupIdByIndex = (groupIndex) => {
    // eslint-disable-next-line no-unused-vars
    const { rows, ...group } = grid.value[groupIndex];

    return group;
  };

  const addGridItem = (
    item,
    _rowIndex = null,
    _weekIndex = null,
    _groupIndex = null,
  ) => {
    if (
      item?.planningMeta.rowIndex === undefined ||
      item?.planningMeta?.weekIndexes === undefined ||
      item.planningMeta.weekIndexes.length === 0
    ) {
      throw new Error(
        "Row index and week indexes are required to add an item to the grid",
      );
    }

    try {
      const {
        duration,
        subject_shortage,
        planningMeta: { weekIndexes, rowIndex, groupId },
      } = item;

      // Prefer the parameters over the item's values
      _rowIndex = !isNaN(parseInt(_rowIndex)) ? _rowIndex : rowIndex;
      _weekIndex = !isNaN(parseInt(_weekIndex)) ? _weekIndex : weekIndexes[0];
      _groupIndex = !isNaN(parseInt(_groupIndex))
        ? _groupIndex
        : findGridGroupIndex(groupId, subject_shortage);

      let currentRow = grid.value[_rowIndex];

      if (hasGridGroups.value) {
        currentRow = grid.value[_groupIndex].rows[_rowIndex];

        // Update the group index and id
        item.updateGroupIndex(_groupIndex);
      }

      // Detect clusters overlapping the target week span. Conflicts and the
      // insert position are resolved in week space (not array index), so a
      // start week covered by a cluster (drag-left) and index skew from earlier
      // multi-week clusters are both handled correctly.
      const conflictingItems = getRowConflicts(
        currentRow,
        _weekIndex,
        duration,
      ).filter((conflictingItem) => conflictingItem instanceof Cluster);

      if (conflictingItems.length > 0) {
        conflictingItems.forEach((item) => {
          item.onDragStart().onDragDrop(null, {
            rowIndex: item.planningMeta.rowIndex + 1,
            weekIndex: item.planningMeta.weekIndexes[0],
            groupIndex: item.planningMeta.groupIndex,
          });
        });
      }

      // Bumping the conflicts above replaced them with single-week empty cells,
      // so the span is now contiguous cells; locate the start week's slot.
      const foundWeekIndex = findRowInsertIndex(currentRow, _weekIndex);

      currentRow.splice(foundWeekIndex, duration, item);

      if (hasGridGroups.value) {
        const isLastIndex =
          _rowIndex === grid.value[_groupIndex].rows.length - 1;

        if (isLastIndex) {
          const generatedRow = generateClusterRow(
            grid.value[_groupIndex].rows.length,
            _groupIndex,
            {
              groupId,
              subjectShortage: subject_shortage,
              educationLevel: grid.value[_groupIndex].meta.educationLevel,
            },
          );

          grid.value[_groupIndex].rows.push(generatedRow);
        }
      }
    } catch (error) {
      console.log("Cluster kan niet toegevoegd worden want: ", error);
    }
  };

  const generateRowSize = (items) => {
    if (items.every((item) => !item.planningMeta.rowIndex)) {
      return 0;
    }

    return Math.max(
      ...items.flatMap(({ planningMeta: { rowIndex } }) => rowIndex),
    );
  };

  const removeWeekFromRow = (rowIndex, weekIndex, groupIndex = null) => {
    const foundIndex = findWeekIndex(rowIndex, weekIndex, groupIndex);

    if (groupIndex === null) {
      return grid.value[rowIndex].splice(foundIndex, 1);
    }

    return grid.value[groupIndex].rows[rowIndex].splice(foundIndex, 1);
  };

  const addWeekToRow = (model, rowIndex, weekIndex, atEnd = false) => {
    let foundIndex = -1;
    let groupIndex = null;

    if (!hasGridGroups.value) {
      foundIndex = findClusterIndex(rowIndex, model.id);
    } else {
      groupIndex = model.planningMeta.groupIndex;

      foundIndex = findClusterIndex(rowIndex, model.id, groupIndex);
    }

    weekIndex = atEnd ? weekIndex + 1 : weekIndex - 1;
    foundIndex = atEnd ? foundIndex + 1 : foundIndex;

    if (!hasGridGroups.value) {
      return grid.value[rowIndex].splice(foundIndex, 0, {
        weekIndex,
        rowIndex,
      });
    }

    const weekToAdd = generateWeekArray(rowIndex, [weekIndex], groupIndex, {
      groupId: model.planningMeta.groupId,
      subjectShortage: model.subject_shortage,
      educationLevel: grid.value[groupIndex].meta.educationLevel,
    })[0];

    return grid.value[groupIndex].rows[rowIndex].splice(
      foundIndex,
      0,
      weekToAdd,
    );
  };

  const findWeekIndex = (rowIndex, weekIndex, groupIndex = null) => {
    let foundIndex = -1;

    if (groupIndex === null) {
      foundIndex = grid.value[rowIndex].findIndex(
        ({ weekIndex: _weekIndex }) => _weekIndex === weekIndex,
      );
    } else {
      foundIndex = grid.value[groupIndex].rows[rowIndex].findIndex(
        ({ weekIndex: _weekIndex }) => _weekIndex === weekIndex,
      );
    }

    if (foundIndex === -1) {
      throw new Error(
        `Week with index ${weekIndex} not found in row ${rowIndex}, skipping removal.`,
      );
    }

    return foundIndex;
  };

  const removeItemFromGrid = (rowIndex, weekIndexes, model = null) => {
    if (!model) {
      throw new Error("Model is required to remove an item from the grid");
    }

    if (!hasGridGroups.value) {
      // This will generate a new array with the weeks that should fill the gaps from the removed model
      const weeks = generateWeekArray(rowIndex, weekIndexes);

      const clusterIndex = findClusterIndex(rowIndex, model.id);

      // Replace the model with the weeks array
      return grid.value[rowIndex].splice(clusterIndex, 1, ...weeks);
    }

    const groupIndex = findGridGroupIndex(
      model.planningMeta.groupId,
      model.subject_shortage,
    );

    // This will generate a new array with the weeks that should fill the gaps from the removed model
    const weeks = generateWeekArray(rowIndex, weekIndexes, groupIndex, {
      groupId: model.planningMeta.groupId,
      subjectShortage: model.subject_shortage,
      educationLevel: grid.value[groupIndex].meta.educationLevel,
    });

    const clusterIndex = findClusterIndex(rowIndex, model.id, groupIndex);

    grid.value[groupIndex].rows[rowIndex].splice(clusterIndex, 1, ...weeks);

    return;
  };

  // Drop trailing all-empty rows after a cluster is removed, keeping one empty
  // row past the last cluster and never fewer than DEFAULT_VISIBLE_CLUSTER_ROWS.
  // Without this, adding then discarding a cluster leaves the extra row that
  // addGridItem generated, so the class stays one row too tall.
  const trimTrailingEmptyRows = (model) => {
    if (!hasGridGroups.value) {
      return;
    }

    const groupIndex = findGridGroupIndex(
      model.planningMeta.groupId,
      model.subject_shortage,
    );
    const rows = grid.value[groupIndex].rows;

    let lastClusterRow = -1;

    rows.forEach((row, index) => {
      if (row.some((cell) => cell instanceof Cluster)) {
        lastClusterRow = index;
      }
    });

    const desiredRows = Math.max(
      lastClusterRow + 1 + DEFAULT_EMPTY_ROWS,
      DEFAULT_VISIBLE_CLUSTER_ROWS,
    );

    if (rows.length > desiredRows) {
      rows.splice(desiredRows);
    }
  };

  const findGridGroupIndex = (groupId, subject_shortage) => {
    const foundIndex = grid.value.findIndex(
      ({ id, meta: { subject } }) =>
        id === groupId && subject === subject_shortage,
    );

    if (foundIndex === -1) {
      throw new Error(`Group with id ${groupId} not found in the grid`);
    }

    return foundIndex;
  };

  const findClusterIndex = (rowIndex, modelId, groupIndex = null) => {
    let foundIndex = -1;

    if (groupIndex !== null) {
      foundIndex = grid.value[groupIndex].rows[rowIndex].findIndex(
        ({ id }) => id === modelId,
      );
    } else {
      foundIndex = grid.value[rowIndex].findIndex(({ id }) => id === modelId);
    }

    if (foundIndex === -1) {
      throw new Error(
        `Cluster with id ${modelId} not found in row ${rowIndex}, skipping removal.`,
      );
    }

    return foundIndex;
  };

  return {
    grid,
    isInitialized,
    hasGridGroups,
    isRendered,
    initializeGrid,
    generateRowSize,
    generateGridGroup,
    removeWeekFromRow,
    addWeekToRow,
    removeItemFromGrid,
    addGridItem,
    trimTrailingEmptyRows,
    rebuildGridFromClusters,
    rebuildGroupFromClusters,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
  };
};
